// Las palabras con que el hilo de observación nombra a sus actores y a su
// objeto, por receta (ADR-0134, Fase 4). Antes estaban fijas en el panel y
// en el historial —«Jefe de postventa», «Propietario», «Contratista»,
// «unidad»— y facility, que ahora corre el mismo hilo, las heredaba.
//
// De dónde sale cada una, sin ramas por slug (fitness no-recipe-slug-branches):
//   - el carril asset_party / assigning_party → el `label` de los roles
//     vivos (`memberRoles`, sin `retired`) que declaran ese carril:
//     postventa «Propietario», facility «Jefe de tienda» / «Gerente» y
//     «Supervisor»;
//   - el objeto → `labels.asset` («Unidad», «Tienda»);
//   - «tiene la unidad activada» sólo existe si la receta modela un ciclo
//     de vida del asset (`assetStatuses`);
//   - quién recibe el enlace: la receta no tiene todavía una etiqueta para
//     él. Es «contratista» en toda vertical (decisión del founder,
//     2026-10-30: facility lo llamaba «proveedor» y se unificó con
//     postventa), así que hoy es una constante. TODO(#0719): una etiqueta
//     de receta (`labels.assignee`) cuando el schema la admita.

import { closureLanes, type ObservationStatusActor } from "@bitacorapro/core-engine";
import type { RecipeMeResponse } from "@bitacorapro/api-types";

type WordsRecipe = Pick<RecipeMeResponse, "memberRoles" | "labels" | "assetStatuses"> &
  Partial<Pick<RecipeMeResponse, "dashboards" | "observationClosure" | "observationStatuses">>;

export type ThreadWords = {
  /** «Contratista». */
  assignee: string;
  /** «contratista». */
  assigneeLower: string;
  /** «Contratistas» (nav, títulos). */
  assigneePlural: string;
  /** «contratistas». */
  assigneePluralLower: string;
  /** «Propietario» / «Jefe de tienda» — el rol del carril asset_party. */
  assetParty: string;
  /** «el propietario» / «el jefe de tienda». */
  elAssetParty: string;
  /** «Unidad» / «Tienda». */
  asset: string;
  /** «unidad» / «tienda». */
  assetLower: string;
  /** Quién asigna, en minúscula y con artículo: «el gerente de postventa
   *  o el jefe de proyecto» / «el gerente o el supervisor». */
  assigningParty: string;
  /** Quién da el veredicto sobre lo resuelto: «el propietario» /
   *  «el jefe de tienda, el gerente o el supervisor». */
  closers: string;
  /** True cuando sólo cierra asset_party: la reapertura tiene un autor
   *  nombrable («reabierta por el propietario»). */
  singleAssetPartyCloser: boolean;
  /** La receta modela la activación del asset (`assetStatuses`). */
  assetActivation: boolean;
};

function lower(s: string): string {
  return s.charAt(0).toLowerCase() + s.slice(1);
}

/** «a», «a o b», «a, b o c». */
function joinO(items: string[]): string {
  if (items.length <= 1) return items[0] ?? "";
  return `${items.slice(0, -1).join(", ")} o ${items[items.length - 1]}`;
}

/** Los `label` de los roles vivos que declaran el carril. */
export function laneRoleLabels(
  recipe: Pick<RecipeMeResponse, "memberRoles"> | undefined,
  lane: ObservationStatusActor
): string[] {
  return (recipe?.memberRoles ?? [])
    .filter((r) => r.observationStatusActor === lane && r.retired !== true)
    .map((r) => r.label);
}

export function threadWords(recipe: WordsRecipe | undefined): ThreadWords {
  const assignee = "Contratista";
  const assigneePlural = "Contratistas";
  const assetPartyRoles = laneRoleLabels(recipe, "asset_party");
  const assetParty = assetPartyRoles[0] ?? recipe?.labels?.assetInfoField ?? "Propietario";
  const elAssetParty = `el ${lower(assetParty)}`;
  const assigning = laneRoleLabels(recipe, "assigning_party").map((l) => `el ${lower(l)}`);
  const lanes = recipe ? closureLanes(recipe) : [];
  const closerNouns = lanes.flatMap((lane) =>
    lane === "asset_party"
      ? [elAssetParty]
      : laneRoleLabels(recipe, lane).map((l) => `el ${lower(l)}`)
  );
  const asset = recipe?.labels?.asset ?? "Unidad";
  return {
    assignee,
    assigneeLower: lower(assignee),
    assigneePlural,
    assigneePluralLower: lower(assigneePlural),
    assetParty,
    elAssetParty,
    asset,
    assetLower: lower(asset),
    assigningParty: joinO(assigning.length > 0 ? assigning : ["el equipo a cargo"]),
    closers: joinO(closerNouns.length > 0 ? closerNouns : [elAssetParty]),
    singleAssetPartyCloser: lanes.length === 1 && lanes[0] === "asset_party",
    assetActivation: (recipe?.assetStatuses?.length ?? 0) > 0,
  };
}

/** Con mayúscula inicial, para abrir una oración. */
export function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
